import type { ColumnVisibilityItem } from "providers/Auth";

/**
 * Column order rules shared by every Partner Portal table whose columns users can reorder
 * (CRM-1891). The API applies the same rules when it normalizes a saved tab
 * (Boom::Partner::User#normalize_tab_columns), so both sides agree on where a new column lands.
 *
 * "Boom's standard order" is the order of a table's defaults. A saved list whose columns are still
 * in that relative order counts as never reordered: new columns slot into their standard place.
 * Once the user has moved columns, new ones go to the end so they never wedge themselves between
 * columns the user arranged.
 */

const ids = (columns: { id: string }[]) => columns.map((column) => column.id);

export const isStandardOrder = (
  columns: { id: string }[],
  defaults: { id: string }[]
): boolean => {
  const savedIds = ids(columns);
  const present = new Set(savedIds);
  const standard = ids(defaults).filter((id) => present.has(id));
  return (
    savedIds.length === standard.length &&
    savedIds.every((id, index) => id === standard[index])
  );
};

type MergeOptions = {
  /** How a column the saved list predates is added (defaults to its default state). */
  missingColumn?: (column: ColumnVisibilityItem) => ColumnVisibilityItem;
};

/**
 * Merge a saved column list with the table's defaults. Shown/hidden and order come from the saved
 * list; label, `always_on` and `disabled` always come from the defaults, and `always_on` columns
 * stay shown. Saved columns the table no longer has are dropped.
 */
export const mergeSavedColumns = (
  saved: ColumnVisibilityItem[] | undefined,
  defaults: ColumnVisibilityItem[],
  { missingColumn = (column) => column }: MergeOptions = {}
): ColumnVisibilityItem[] => {
  if (!saved?.length) return defaults;

  const defaultsById = new Map(defaults.map((column) => [column.id, column]));
  const seen = new Set<string>();
  const kept: ColumnVisibilityItem[] = [];
  saved.forEach((column) => {
    const def = defaultsById.get(column.id);
    if (!def || seen.has(column.id)) return;
    seen.add(column.id);
    kept.push({
      ...def,
      checked: def.always_on ? true : column.checked
    });
  });

  const missing = defaults
    .filter((column) => !seen.has(column.id))
    .map(missingColumn);

  if (!isStandardOrder(kept, defaults)) return [...kept, ...missing];

  const merged = new Map(
    [...kept, ...missing].map((column) => [column.id, column])
  );
  return defaults.map((column) => merged.get(column.id) ?? column);
};

/**
 * Re-arrange the columns in `subsetOrder` among the slots they already occupy in `columns`; every
 * other column keeps its place. Used when only part of a table's columns took part in a move (the
 * ones on screen, or one section of the Columns menu).
 */
export const reorderWithinSlots = <T extends { id: string }>(
  columns: T[],
  subsetOrder: string[]
): T[] => {
  const inSubset = new Set(subsetOrder);
  const byId = new Map(columns.map((column) => [column.id, column]));
  const queue = subsetOrder
    .map((id) => byId.get(id))
    .filter((column): column is T => Boolean(column));
  let next = 0;
  return columns.map((column) =>
    inSubset.has(column.id) && next < queue.length ? queue[next++] : column
  );
};

/**
 * Fold a change made to part of the columns (a table that hides some of them from its Columns
 * menu, e.g. a feature the account lacks) back into the full list: the listed columns take their
 * new shown/hidden state and order among their own slots, the others stay untouched.
 */
export const applyColumnsSubset = <T extends ColumnVisibilityItem>(
  columns: T[],
  subset: ColumnVisibilityItem[]
): T[] => {
  const checkedById = new Map(
    subset.map((column) => [column.id, column.checked])
  );
  const updated = columns.map((column) =>
    checkedById.has(column.id)
      ? { ...column, checked: checkedById.get(column.id) ?? column.checked }
      : column
  );
  return reorderWithinSlots(updated, ids(subset));
};

/** Move one column so it sits right before `beforeId`, or right after `afterId` when that is null. */
export const moveColumn = <T extends { id: string }>(
  columns: T[],
  moved: T,
  target: { beforeId: string } | { afterId: string | null }
): T[] => {
  const rest = columns.filter((column) => column.id !== moved.id);
  if ("beforeId" in target) {
    const index = rest.findIndex((column) => column.id === target.beforeId);
    if (index !== -1)
      return [...rest.slice(0, index), moved, ...rest.slice(index)];
    return [...rest, moved];
  }
  if (target.afterId === null) return [moved, ...rest];
  const index = rest.findIndex((column) => column.id === target.afterId);
  if (index === -1) return [...rest, moved];
  return [...rest.slice(0, index + 1), moved, ...rest.slice(index + 1)];
};
